import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {
  ExportCheckpointDto,
  SaleOfferAcceptRequest,
  SaleOfferDto,
  SaleOfferItemDto,
  TradeRecordDto
} from '../../../../models/generated-models';
import {ConfirmDialogService} from '../../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../../shared/commons/toast/toast-service';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {TradeApiService} from '../trade-api.service';
import {TradeStore} from '../trade.store';
import {AddBusinessFlowService} from '../../user-business/add/add-business-flow.service';
import {TradeBusinessSelectComponent} from '../trade-business-select.component';
import {
  DEFAULT_TRADE_UNIT,
  EXPORTER_BUSINESS_TYPE_TITLE,
  TRADE_BASE,
  isSaleOfferCode,
  tradeKindLabel
} from '../trade.constants';
import {
  formatQuantity,
  formatQuantityWithUnit,
  normalizeQuantityInput,
  offerComponentsBreakdown,
  parseQuantity
} from '../trade-utils';

/**
 * Accepts a sale offer after scanning the seller's QR. The lines come from the
 * offer; the buyer types the quantity actually taken per line (at most what the
 * seller wrote, the full line by default) and the backend returns the untaken
 * remainder to the seller's stock. Every acceptance is a PURCHASE (one record
 * per line).
 *
 * A ผู้ส่งออก business is a normal node whose record ends the chain: it fills the
 * export attributes (checkpoint, exporter registration, driver phone) and the
 * goods leave the chain instead of being credited to its stock.
 *
 * A `T-`/`S-` lot or stock code is no longer a buying path: the seller has to
 * create a sale offer first.
 */
@Component({
  selector: 'app-trade-receive',
  standalone: true,
  imports: [FormsModule, RouterLink, ThaiDateTimePipe, TradeBusinessSelectComponent],
  templateUrl: './trade-receive.component.html',
  styleUrl: './trade-receive.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeReceiveComponent implements OnInit {
  private readonly api = inject(TradeApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  protected readonly store = inject(TradeStore);
  private readonly addBusinessFlow = inject(AddBusinessFlowService);

  protected readonly unit = DEFAULT_TRADE_UNIT;

  protected readonly code = signal('');
  protected readonly offer = signal<SaleOfferDto | null>(null);
  /** A scanned lot/stock code: shown as an explanation, never as a buy form. */
  protected readonly lotRecord = signal<TradeRecordDto | null>(null);
  /** The seller's open offers behind a scanned lot QR when there is more than one to pick from. */
  protected readonly lotOffers = signal<SaleOfferDto[]>([]);
  /** Set when the offer on show was reached through a lot / stock QR rather than its own QR. */
  protected readonly viaLot = signal<TradeRecordDto | null>(null);
  protected readonly loading = signal(false);
  protected readonly notFound = signal(false);
  protected readonly saving = signal(false);
  protected readonly submitted = signal(false);

  /** The buyer's own stock name per offer line, in line order. */
  protected readonly productNames = signal<string[]>([]);

  /** The quantity the buyer actually takes per offer line, in line order (raw input text). */
  protected readonly quantities = signal<string[]>([]);

  protected form = {
    truckNo: '',
    truckSide: '' as '' | 'BUYER' | 'SELLER',
    contactPhone: '',
    exportCheckpointId: '',
    exporterRegistrationNo: '',
    /** Exporter only: pin the record at the seller instead of at this business (work item 179). */
    useSellerLocation: false,
    containerNo: '',
    sealNo: '',
    qualityNote: ''
  };

  protected readonly tradeKindLabel = tradeKindLabel;
  protected readonly formatQuantity = formatQuantity;
  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;
  protected readonly offerComponentsBreakdown = offerComponentsBreakdown;
  protected readonly tradeBase = TRADE_BASE;

  /** Product names already in the buyer business's stock, for the pooling datalist. */
  protected readonly stockProductNames = signal<string[]>([]);

  /** The ด่านส่งออก master, only needed by an exporter. */
  protected readonly checkpoints = signal<ExportCheckpointDto[]>([]);

  /** A ผู้ส่งออก business buys like anyone else, but its record ends the chain. */
  protected readonly isExporterBusiness = computed(() =>
    (this.store.selectedBusiness()?.businessTypeTitles ?? [])
      .some((title) => (title ?? '').trim() === EXPORTER_BUSINESS_TYPE_TITLE)
  );

  /**
   * Typed ผู้ส่งออก and nothing else: it neither moves goods on nor pools them, so the truck,
   * container, seal and quality inputs are not asked for (the backend waives them the same way).
   */
  protected readonly exporterOnlyBusiness = computed(() => {
    const titles = (this.store.selectedBusiness()?.businessTypeTitles ?? []).map((title) => (title ?? '').trim());
    return titles.length > 0 && titles.every((title) => title === EXPORTER_BUSINESS_TYPE_TITLE);
  });

  protected readonly items = computed<SaleOfferItemDto[]>(() => this.offer()?.items ?? []);

  /** What the seller wrote on the whole offer. */
  protected readonly offerTotalQuantity = computed(
    () => this.offer()?.totalQuantity
      ?? this.items().reduce((sum, item) => sum + (item.quantity ?? 0), 0)
  );

  /** What the buyer is about to take: the sum of the per-line quantities. */
  protected readonly totalQuantity = computed(
    () => this.items().reduce((sum, item, index) => sum + parseQuantity(this.quantityAt(index)), 0)
  );

  /**
   * One message per line, empty when the line is fine. A line must be a number
   * above 0 and may not go over what the seller offered.
   */
  protected readonly quantityErrors = computed<string[]>(() =>
    this.items().map((item, index) => {
      const raw = (this.quantities()[index] ?? '').trim();
      const value = parseQuantity(raw);
      if (!raw || value <= 0) {
        return 'กรุณาระบุจำนวน';
      }
      const offered = item.quantity ?? 0;
      if (offered > 0 && value > offered) {
        return `ใส่ได้ไม่เกิน ${formatQuantity(offered)} กก.`;
      }
      return '';
    })
  );

  protected readonly quantitiesValid = computed(() => this.quantityErrors().every((error) => !error));

  /** The remainder goes back to the seller's stock, so say so before the buyer confirms. */
  protected readonly partialReceive = computed(
    () => this.quantitiesValid() && this.totalQuantity() < this.offerTotalQuantity()
  );

  /** Accepting an own offer is rejected by the backend. */
  protected readonly sameBusiness = computed(() => {
    const businessId = this.store.selectedBusinessId();
    return businessId != null && businessId === this.offer()?.businessId;
  });

  protected readonly canSubmit = computed(() =>
    this.offer() != null
    && this.store.selectedBusinessId() != null
    && !this.sameBusiness()
    && this.quantitiesValid()
  );

  ngOnInit(): void {
    const code = this.route.snapshot.queryParamMap.get('code')?.trim() ?? '';
    this.code.set(code);
    void this.store.loadBusinesses();
    this.api.listExportCheckpoints().subscribe({
      next: (items) => this.checkpoints.set(items ?? []),
      error: () => this.checkpoints.set([])
    });
    if (code) {
      this.resolve(code);
    } else {
      this.notFound.set(true);
    }
  }

  protected onBusinessChange(businessId: number | null): void {
    this.store.selectBusiness(businessId);
    this.loadStockNames(businessId);
  }

  protected productNameAt(index: number): string {
    return this.productNames()[index] ?? '';
  }

  protected onProductNameChange(index: number, value: string): void {
    this.productNames.update((names) => names.map((name, at) => (at === index ? value : name)));
  }

  protected quantityAt(index: number): string {
    return this.quantities()[index] ?? '';
  }

  protected onQuantityChange(index: number, value: string | number | null): void {
    const next = normalizeQuantityInput(value == null ? '' : `${value}`);
    this.quantities.update((values) => values.map((current, at) => (at === index ? next : current)));
  }

  /** The inline message for a line, empty while the line is fine. */
  protected quantityError(index: number): string {
    return this.quantityErrors()[index] ?? '';
  }

  /** Every purchase leg names the truck (mandatory) … */
  protected truckMissing(): boolean {
    return !this.exporterOnlyBusiness() && !this.form.truckNo.trim();
  }

  /** … and whose truck it was. */
  protected truckSideMissing(): boolean {
    return !this.exporterOnlyBusiness() && !this.form.truckSide;
  }

  // --- export validation (a ผู้ส่งออก business only) -------------------------

  protected checkpointMissing(): boolean {
    return this.isExporterBusiness() && !this.form.exportCheckpointId;
  }

  protected exporterRegistrationMissing(): boolean {
    return this.isExporterBusiness() && !this.form.exporterRegistrationNo.trim();
  }

  private exportFieldsMissing(): boolean {
    return this.checkpointMissing() || this.exporterRegistrationMissing();
  }

  /** The picked checkpoint, used by the confirmation text. */
  protected selectedCheckpoint(): ExportCheckpointDto | null {
    const id = Number(this.form.exportCheckpointId);
    if (!Number.isInteger(id)) {
      return null;
    }
    return this.checkpoints().find((item) => item.id === id) ?? null;
  }

  protected submit(): void {
    this.submitted.set(true);
    if (this.saving() || !this.canSubmit() || this.truckMissing() || this.truckSideMissing() || this.exportFieldsMissing()) {
      return;
    }
    const offerId = this.offer()?.id;
    if (offerId == null) {
      return;
    }
    const businessTitle = this.store.selectedBusiness()?.title ?? '';
    const checkpointName = this.selectedCheckpoint()?.name ?? '';
    const exportSuffix = this.isExporterBusiness() ? ` เพื่อส่งออกผ่าน ${checkpointName}` : '';

    void firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการบันทึก',
      content: `ยืนยันรับซื้อสินค้า ${this.items().length} รายการ `
        + `รวม ${formatQuantityWithUnit(this.totalQuantity(), this.unit)} `
        + `ในนาม ${businessTitle}${exportSuffix} หรือไม่`,
      confirmButtonMsg: 'ยืนยัน',
      closeButtonMsg: 'ยกเลิก'
    })).then((confirmed) => {
      if (confirmed) {
        this.save(offerId);
      }
    }).catch(() => undefined);
  }

  private save(offerId: number): void {
    this.saving.set(true);
    const businessId = this.store.selectedBusinessId() ?? undefined;
    // Every field but the business is optional — the lines come from the offer.
    const request: SaleOfferAcceptRequest = {
      businessId,
      items: this.items().map((item, index) => ({
        offerItemId: item.id,
        productName: this.productNameAt(index).trim() || undefined,
        quantity: parseQuantity(this.quantityAt(index))
      })),
      contactPhone: this.form.contactPhone.trim() || undefined,
      truckNo: this.exporterOnlyBusiness() ? undefined : this.form.truckNo.trim(),
      truckSide: this.exporterOnlyBusiness() ? undefined : this.form.truckSide || undefined
    };
    if (this.isExporterBusiness()) {
      // The goods leave the chain here, so the export attributes travel with the record.
      request.exportCheckpointId = Number(this.form.exportCheckpointId);
      request.exporterRegistrationNo = this.form.exporterRegistrationNo.trim() || undefined;
      request.useSellerLocation = this.form.useSellerLocation;
      if (!this.exporterOnlyBusiness()) {
        request.containerNo = this.form.containerNo.trim() || undefined;
        request.sealNo = this.form.sealNo.trim() || undefined;
        request.qualityNote = this.form.qualityNote.trim() || undefined;
      }
    }

    this.api.acceptOffer(offerId, request).subscribe({
      next: (accepted) => {
        this.saving.set(false);
        // Land the buyer on their own side of the trade — the purchase record when
        // there is one, otherwise the stock list — not on the seller's ใบขาย.
        const recordIds = accepted.acceptedRecordIds ?? [];
        const count = recordIds.length || this.items().length;
        this.toast.success(`รับสินค้าเข้าสต็อกแล้ว ${count} รายการ`);
        if (recordIds.length === 1) {
          void this.router.navigate([TRADE_BASE, recordIds[0]]);
        } else {
          const addedStock = this.items()
            .map((_, index) => ({
              productName: this.productNameAt(index).trim(),
              quantity: parseQuantity(this.quantityAt(index))
            }))
            .filter((line) => line.productName !== '');
          void this.router.navigate([TRADE_BASE], {state: {addedStock}});
        }
      },
      error: () => this.saving.set(false)
    });
  }

  /**
   * An `O-` code can only be an offer; anything else is tried as an offer first
   * and then as a lot/stock code, so the scanner can stay dumb.
   */
  private resolve(code: string): void {
    this.loading.set(true);
    this.notFound.set(false);
    this.lotRecord.set(null);
    this.lotOffers.set([]);
    this.viaLot.set(null);
    this.api.lookupOffer(code).subscribe({
      next: (offer) => {
        this.offer.set(offer);
        this.fillLines(offer);
        this.loading.set(false);
        this.loadStockNames(this.store.selectedBusinessId());
      },
      error: () => {
        this.offer.set(null);
        if (isSaleOfferCode(code)) {
          this.notFound.set(true);
          this.loading.set(false);
          return;
        }
        this.lookupLot(code);
      }
    });
  }

  /**
   * A lot / stock QR (the "แชร์ QR รายการ" one) is what sellers hand out most, so it must lead
   * somewhere (work item 169): to the seller's open offer for that product when there is exactly
   * one, to a picker when there are several, and to a clear "ask the seller to create an offer"
   * note only when there is none.
   */
  private lookupLot(code: string): void {
    this.api.lookup(code).subscribe({
      next: (record) => {
        this.lotRecord.set(record);
        this.api.listOffersForCode(code).subscribe({
          next: (offers) => {
            const open = offers ?? [];
            if (open.length === 1) {
              this.useOffer(open[0], record);
              return;
            }
            this.lotOffers.set(open);
            this.loading.set(false);
          },
          error: () => {
            this.lotOffers.set([]);
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.lotRecord.set(null);
        this.notFound.set(true);
        this.loading.set(false);
      }
    });
  }

  /** One of the seller's offers behind the scanned lot: from here on it is a normal accept. */
  protected useOffer(offer: SaleOfferDto, lot: TradeRecordDto | null = this.lotRecord()): void {
    this.viaLot.set(lot);
    this.lotRecord.set(null);
    this.lotOffers.set([]);
    this.offer.set(offer);
    this.fillLines(offer);
    this.loading.set(false);
    this.loadStockNames(this.store.selectedBusinessId());
  }

  /** Every line starts as the seller wrote it: same stock name, full quantity. */
  private fillLines(offer: SaleOfferDto): void {
    const items = offer.items ?? [];
    this.productNames.set(items.map((item) => item.productName ?? ''));
    this.quantities.set(items.map((item) => (item.quantity != null ? `${item.quantity}` : '')));
  }

  /** The seller may have created the offer since the scan: look the same code up again. */
  protected retry(): void {
    const code = this.code().trim();
    if (code) {
      this.resolve(code);
    }
  }

  private loadStockNames(businessId: number | null): void {
    if (businessId == null) {
      this.stockProductNames.set([]);
      return;
    }
    this.api.listStock(businessId).subscribe({
      next: (items) => this.stockProductNames.set(
        [...new Set((items ?? [])
          .map((item) => item.productName?.trim())
          .filter((name): name is string => !!name))]
      ),
      error: () => this.stockProductNames.set([])
    });
  }

  /** Same flow as "+ เพิ่มธุรกิจ" on the business page: choose the form flavour, then the add form. */
  protected addBusiness(): void {
    this.addBusinessFlow.start();
  }
}
